import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { LoaderCircle } from "lucide-react";
import { useState } from "react";

import { AuthCard } from "@/components/auth-card";
import { FormField } from "@/components/form-field";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  acceptInvitation,
  errorMessage,
  fetchInvitation,
  type InvitationInfo,
  MIN_PASSWORD_CHARS,
  ROLE_LABELS,
} from "@/lib/api";
import { meQuery, passwordProblem } from "@/lib/auth";
import { BackToLogin } from "@/routes/forgot-password";

/**
 * `/einladung?token=…` (AUTH-04): a new user sets name and password; an
 * existing account confirms with its password and joins the tenant.
 */
export function InvitationPage({ token }: { token: string }) {
  const invitation = useQuery({
    queryKey: ["invitation", token],
    queryFn: () => fetchInvitation(token),
    retry: false,
    staleTime: Infinity,
  });

  if (invitation.isPending) {
    return (
      <AuthCard title="Einladung">
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <LoaderCircle className="animate-spin" aria-hidden /> Wird geladen …
        </p>
      </AuthCard>
    );
  }
  if (invitation.isError) {
    return (
      <AuthCard title="Einladung">
        <div className="grid gap-4">
          <Alert variant="destructive">{errorMessage(invitation.error)}</Alert>
          <p className="text-sm text-muted-foreground">
            Bitte die Person, die dich eingeladen hat, um eine neue Einladung.
          </p>
          <BackToLogin />
        </div>
      </AuthCard>
    );
  }
  return <AcceptForm token={token} invitation={invitation.data} />;
}

function AcceptForm({ token, invitation }: { token: string; invitation: InvitationInfo }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const existing = invitation.account_exists;
  const mutation = useMutation({
    mutationFn: () => acceptInvitation(token, password, existing ? undefined : name),
    onSuccess: async (me) => {
      queryClient.clear();
      queryClient.setQueryData(meQuery.queryKey, me);
      await navigate({ to: "/" });
    },
  });

  return (
    <AuthCard
      title={`Einladung zu ${invitation.tenant}`}
      description={
        <>
          Für {invitation.email}, Rolle {ROLE_LABELS[invitation.role]}.{" "}
          {existing
            ? "Du hast schon ein Konto; bestätige mit deinem Passwort."
            : "Lege deinen Namen und ein Passwort fest."}
        </>
      }
    >
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          const found = existing ? null : passwordProblem(password, repeat);
          setProblem(found);
          if (!found) mutation.mutate();
        }}
      >
        {(problem ?? mutation.isError) && (
          <Alert variant="destructive">{problem ?? errorMessage(mutation.error)}</Alert>
        )}
        {existing ? (
          <FormField
            label="Dein Passwort"
            type="password"
            autoComplete="current-password"
            required
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        ) : (
          <>
            <FormField
              label="Name"
              autoComplete="name"
              required
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <FormField
              label="Passwort"
              type="password"
              autoComplete="new-password"
              required
              hint={`Mindestens ${MIN_PASSWORD_CHARS} Zeichen; ein Satz ist leichter zu merken.`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <FormField
              label="Passwort wiederholen"
              type="password"
              autoComplete="new-password"
              required
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
            />
          </>
        )}
        <Button type="submit" disabled={mutation.isPending}>
          Einladung annehmen
        </Button>
      </form>
    </AuthCard>
  );
}
